import { datadogRum } from '@datadog/browser-rum';
import { groupBy, sortBy } from 'lodash';
import { useCallback } from 'react';
import { isTestTeamName } from '@/common/utils/isTestTeamName';
import { unique } from '@/helpers';
import type { DropdownOption } from '@/views/Community/views/CommunityListings/components/FilterButton/FilterDialog/LocationAutocomplete/LocationAutocomplete.helpers';
import { identityApi } from '$api-clients/clients';
import { MAX_NUM_DROPDOWN_OPTIONS } from '../../components/LocationSearchBar/LocationAutocomplete/helpers';
import type { PartnerMailingAddress } from '../../types';
import { toMailingAddress } from '../../utils/address';
import { mailingAddressHasValidGeolocation } from '../../utils/location';
import { buildTeamLocationsQuery } from '../../utils/teamLocationsQuery';
import {
  resolveOrganizations,
  type TeamListItem,
} from '../useGetTeamOrganization/helpers';

const buildTeamOption = ({
  id,
  legalName,
  alias,
  mailingAddress,
  rootTeamId,
  activeTeamCount,
}: {
  id: string;
  legalName: string;
  alias: string | null;
  mailingAddress: PartnerMailingAddress | null;
  rootTeamId: string;
  activeTeamCount: number;
}): DropdownOption => {
  const hasSubteams = rootTeamId === id && activeTeamCount > 1;

  return {
    id: `team:${id}`,
    parentId: rootTeamId ? `team:${rootTeamId}` : undefined,
    optionType: hasSubteams ? 'parentTeam' : 'team',
    label: [legalName, alias]
      .filter((v) => !!v)
      .filter(unique)
      .join(', '),
    secondaryLabel: [mailingAddress?.city, mailingAddress?.state]
      .filter((v) => !!v)
      .join(', '),
    latitude: Number(mailingAddress?.geolocationLatitude),
    longitude: Number(mailingAddress?.geolocationLongitude),
    group: 'Teams',
    mailingAddress,
  };
};

export type TeamNameSearchResult = {
  options: DropdownOption[];
  injectedParentCount: number;
};

export const useTeamNameSearch = () => {
  // Groups whose root team wasn't itself a search result need that root injected
  // as a header option. Its org was already resolved in the batched walk, so we
  // read it straight from the map rather than fetching again.
  const buildMissingParentTeams = useCallback(
    (
      groupedOptions: Record<string, DropdownOption[]>,
      orgByTeamId: Map<string, TeamListItem[]>,
    ): Record<string, DropdownOption> => {
      const orphanedRootIds = Object.entries(groupedOptions)
        .filter(
          ([groupKey, groupOptions]) =>
            !groupOptions.some((opt) => opt.id === groupKey),
        )
        .map(([groupKey]) => groupKey.slice('team:'.length));

      const parentOptions: Record<string, DropdownOption> = {};
      for (const rootId of orphanedRootIds) {
        const orgTeams = orgByTeamId.get(rootId) ?? [];
        const root = orgTeams.find((team) => !team.parentTeamId) ?? null;
        if (root) {
          parentOptions[`team:${rootId}`] = buildTeamOption({
            id: root.id,
            legalName: root.legalName ?? '',
            alias: root.alias,
            mailingAddress: toMailingAddress(root.mailingAddress),
            rootTeamId: root.id,
            activeTeamCount: orgTeams.filter((team) => team.status === 'ACTIVE')
              .length,
          });
        }
      }
      return parentOptions;
    },
    [],
  );

  const teamNameSearch = useCallback(
    async (searchString: string): Promise<TeamNameSearchResult> => {
      if (!searchString.length) {
        return { options: [], injectedParentCount: 0 };
      }

      const { data, error } = await identityApi.GET('/teams/locations', {
        params: {
          query: {
            q: buildTeamLocationsQuery({
              search: searchString,
              status: 'ACTIVE',
            }),
            limit: MAX_NUM_DROPDOWN_OPTIONS,
          },
        },
      });

      if (error !== undefined) {
        datadogRum.addError(
          'Unable to get team dropdown results in partner map.',
          { error, searchString },
        );

        return { options: [], injectedParentCount: 0 };
      }

      const teams = (data?.data ?? []).filter(
        (team) =>
          (process.env.NEXT_PUBLIC_APP_ENV !== 'prod' ||
            !isTestTeamName(team.legalName)) &&
          mailingAddressHasValidGeolocation(team.mailingAddress),
      );

      // Degrade to ungrouped results if the org walk fails, mirroring the
      // graceful handling of the /teams/locations call above.
      const orgByTeamId = await resolveOrganizations(
        teams.map((team) => team.id),
      ).catch((error) => {
        datadogRum.addError(
          'Failed to resolve team organizations in partner map.',
          { error, searchString },
        );
        return new Map<string, TeamListItem[]>();
      });

      const options: DropdownOption[] = teams.map((team) => {
        const orgTeams = orgByTeamId.get(team.id) ?? [];
        const root = orgTeams.find((orgTeam) => !orgTeam.parentTeamId) ?? null;
        const activeTeamCount = orgTeams.filter(
          (orgTeam) => orgTeam.status === 'ACTIVE',
        ).length;

        return buildTeamOption({
          id: team.id,
          legalName: team.legalName ?? '',
          alias: team.alias,
          mailingAddress: toMailingAddress(team.mailingAddress),
          rootTeamId: root?.id ?? team.id,
          activeTeamCount,
        });
      });

      const groupedOptions = groupBy(
        options,
        (team) => team.parentId ?? team.id,
      );

      const missingParents = buildMissingParentTeams(
        groupedOptions,
        orgByTeamId,
      );
      const injectedParentCount = Object.keys(missingParents).length;

      for (const [groupKey, parentOption] of Object.entries(missingParents)) {
        groupedOptions[groupKey]?.unshift(parentOption);
      }

      const result = Object.values(groupedOptions).flatMap((groupOptions) =>
        sortBy(groupOptions, [
          (opt) => (opt.optionType === 'parentTeam' ? 0 : 1),
        ]),
      );

      return { options: result, injectedParentCount };
    },
    [buildMissingParentTeams],
  );

  return [teamNameSearch];
};
